import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Form, notification, Spin } from 'antd'
import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { VersionForm } from '@/components/form/VersionForm'
import type { VersionFormValues } from '@/components/form/VersionForm'
import {
  createVersion,
  updateVersion,
  getVersionById,
} from '@/api/versions.api'

interface VersionFormDrawerProps {
  isOpen: boolean
  mode: 'create' | 'update'
  versionId: string | null
  onClose: () => void
}

export function VersionFormDrawer({
  isOpen,
  mode,
  versionId,
  onClose,
}: VersionFormDrawerProps) {
  const [form] = Form.useForm<VersionFormValues>()
  const queryClient = useQueryClient()

  const { data: version, isLoading: isFetching } = useQuery({
    queryKey: ['version-detail', versionId],
    queryFn: () =>
      versionId ? getVersionById(versionId) : Promise.resolve(null),
    enabled: isOpen && mode === 'update' && !!versionId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  useEffect(() => {
    if (isOpen) {
      if (mode === 'update' && version) {
        form.setFieldsValue({
          id: version.id,
          name: version.name,
          description: version.description,
          // Dựng lại cấu trúc FileList cho AntD Upload dựa vào Info ID
          apkFile: version.fileApkInfo
            ? [
                {
                  uid: version.fileApkId,
                  name: version.fileApkInfo.originFileName,
                  status: 'done',
                  url: version.fileApkInfo.fileUrl,
                },
              ]
            : [],
          manualFile: version.fileGuildInfo
            ? [
                {
                  uid: version.fileGuildId,
                  name: version.fileGuildInfo.originFileName,
                  status: 'done',
                  url: version.fileGuildInfo.fileUrl,
                },
              ]
            : [],
        })
      } else if (mode === 'create') {
        form.resetFields()
      }
    } else {
      form.resetFields()
    }
  }, [isOpen, mode, version, form])

  const createMutation = useMutation({
    mutationFn: createVersion,
    onSuccess: (response: any) => {
      const successMessage =
        response.responseMessage || 'Thêm mới phiên bản thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['versions'] })
      onClose()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (values: any) => {
      if (!versionId) throw new Error('Missing version ID')
      return updateVersion(versionId, values)
    },
    onSuccess: (response: any) => {
      const successMessage =
        response.responseMessage || 'Cập nhật phiên bản thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['versions'] })
      if (versionId)
        queryClient.invalidateQueries({
          queryKey: ['version-detail', versionId],
        })
      onClose()
    },
  })

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const isDrawerDisabled = isSubmitting || isFetching

  const handleSubmit = (values: VersionFormValues) => {
    const getFileObj = (fileList: any) => {
      if (!fileList || !Array.isArray(fileList) || fileList.length === 0)
        return null
      return fileList[0].originFileObj || null
    }

    const newApkFile = getFileObj(values.apkFile)
    const newGuildFile = getFileObj(values.manualFile)

    if (mode === 'create') {
      if (!newApkFile) {
        notification.error({
          title: 'Vui lòng tải lên tệp cài đặt (APK)!',
        })
        return
      }
      createMutation.mutate({
        name: values.name,
        apkFile: newApkFile,
        guildFile: newGuildFile,
        description: values.description || '',
      })
    } else {
      const isChangeApkFile = !!newApkFile
      const isChangeGuildFile = !!newGuildFile

      const updateData: any = {
        name: values.name,
        isChangeApkFile: isChangeApkFile,
        isChangeGuildFile: isChangeGuildFile,
        description: values.description || '',
        status: version?.status ?? 1,
      }

      if (isChangeApkFile) updateData.apkFile = newApkFile
      if (isChangeGuildFile) updateData.guildFile = newGuildFile

      updateMutation.mutate(updateData)
    }
  }

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'THÊM MỚI PHIÊN BẢN' : 'CẬP NHẬT PHIÊN BẢN'}
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => form.submit()}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            {isSubmitting ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DrawerFooter>
      }
    >
      <Spin spinning={isFetching} description="Đang tải dữ liệu...">
        <div className="min-h-[300px]">
          {(mode === 'create' || version) && (
            <VersionForm form={form} mode={mode} onSubmit={handleSubmit} />
          )}
        </div>
      </Spin>
    </Drawer>
  )
}
